import dayjs from 'dayjs';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  INITIAL_ALLOCATION_OF_COSTS_MEASURE,
  INITIAL_COST_VERSUS_OUTPUT_MEASURES,
} from '../../containers/financials/constants';
import { getRecentlyCompletedMonth } from '../../helpers/general-helpers';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { logger } from '../logger/logger';
import type { State } from './financials-store.type';
import { Charts, ChartType, View, ViewBy } from './financials-store.type';

const useFinancialsStore = create<State>()(
  devtools(
    persist(
      logger((_set) => ({
        view: View.Charts,
        viewBy: ViewBy.Tasks,
        chart: Charts.CostVersusOutput,
        team: null,
        board: null,
        startDate: dayjs().subtract(3, 'month').startOf('month').toDate(),
        endDate: getRecentlyCompletedMonth(),
        workPeriodIds: [],
        filters: {
          costVersusOutput: {
            selectedChartType: ChartType.Line,
            selectedMeasures: INITIAL_COST_VERSUS_OUTPUT_MEASURES,
            selectedTrendLines: [],
          },
          allocationOfCosts: {
            selectedChartType: ChartType.Line,
            selectedMeasure: INITIAL_ALLOCATION_OF_COSTS_MEASURE,
            selectedOptions: [],
            selectedTrendLines: [],
          },
          costByInitiative: {
            selectedChartType: ChartType.Line,
            selectedInitiativeIds: [],
            selectedTrendLines: [],
          },
          agentCosts: {
            selectedChartType: ChartType.Bar,
            viewBy: 'cost' as const,
            selectedCategory: 'general' as const,
            selectedOptions: [],
            selectedTrendLines: [],
            selectedAgentSource: 'all',
          },
        },
        table: {
          selectedYear: null,
          searchTerm: '',
        },
      })),
      {
        name: 'financials-store',
        storage: createSafeJSONStorage(() => localStorage),
        version: 2,
        migrate: (persistedState: any, version: number) => {
          if (version < 2) {
            persistedState.filters = {
              ...persistedState.filters,
              agentCosts: {
                selectedChartType: ChartType.Bar,
                viewBy: 'cost',
                selectedCategory: 'general',
                selectedOptions: [],
                selectedTrendLines: [],
                selectedAgentSource: 'all',
              },
            };
          }
          return persistedState;
        },
        onRehydrateStorage: () => (state) => {
          if (state?.endDate) {
            const endDateDayjs = dayjs(state.endDate);
            const isCurrentMonth = endDateDayjs.isSame(dayjs(), 'month');

            if (isCurrentMonth && state.chart !== Charts.AgentCosts) {
              state.endDate = getRecentlyCompletedMonth();
            }
          }
        },
      },
    ),
  ),
);

export { useFinancialsStore };
